import { adminApiClient } from './adminClient'

export interface AdminUsageStats {
  total_conversations: number
  total_messages: number
  unique_users: number
  feedback_positive: number
  feedback_negative: number
  feedback_positive_rate: number
  filter_jurisdictions: number
  filter_pub_authorities: number
  filter_documents: number
}

export interface FeedbackFilterConfig {
  jurisdictions: { uuid: string; name: string }[]
  pub_authorities: { uuid: string; name: string }[]
  documents: { legislation_uuid: string; title: string }[]
}

export interface AdminFeedbackEntry {
  id: number
  conversation_id: number
  message_id: number
  feedback_type: 'positive' | 'negative'
  user_prompt: string
  bot_response: string
  feedback_text: string | null
  filter_config: FeedbackFilterConfig | null
  user_id: string
  created_at: string
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

// Database tab types
export interface AdminJurisdiction {
  jurisdiction_uuid: string
  jurisdiction_name: string
  jurisdiction_common_name: string
  last_updated: string
}

export interface AdminPubAuthority {
  uuid: string
  code: string
  name: string
  jurisdiction_uuids: string[]
}

export interface AdminDocument {
  legislation_uuid: string
  title: string
  legal_ref: string
  language_code: string
  url_link: string | null
  jurisdiction_uuids: string[]
  publishing_authority_uuids: string[]
  last_updated: string | null
}

// API functions

export async function getAdminStats(): Promise<AdminUsageStats> {
  const response = await adminApiClient.get<AdminUsageStats>('/api/v1/admin/stats')
  return response.data
}

export async function getAdminFeedback(params: {
  page?: number
  page_size?: number
  feedback_type?: string
  sort_order?: string
}): Promise<PaginatedResponse<AdminFeedbackEntry>> {
  const response = await adminApiClient.get<PaginatedResponse<AdminFeedbackEntry>>(
    '/api/v1/admin/feedback',
    { params },
  )
  return response.data
}

export async function getAdminJurisdictions(): Promise<AdminJurisdiction[]> {
  const response = await adminApiClient.get<AdminJurisdiction[]>('/api/v1/admin/database/jurisdictions')
  return response.data
}

export async function getAdminPubAuthorities(): Promise<AdminPubAuthority[]> {
  const response = await adminApiClient.get<AdminPubAuthority[]>('/api/v1/admin/database/pub-authorities')
  return response.data
}

// ─── Ingestion ────────────────────────────────────────────────────────────────

export interface IngestionJob {
  run_id: string
  csv_name: string
  manifest_key: string
  total_chunks: number
  started_at: string
  completed_at: string | null
  job_arn: string | null
  bedrock_status: string | null
  phase: 'preparing' | 'submitting' | 'embedding' | 'processing' | 'completed' | 'failed'
  error: string | null
  has_chunks: boolean
}

export async function getIngestionUploadUrl(filename: string): Promise<{ upload_url: string; file_key: string }> {
  const response = await adminApiClient.post('/api/v1/admin/ingestion/upload-url', null, { params: { filename } })
  return response.data
}

export async function runIngestion(params: {
  file_key: string
  process_as_standards?: boolean
  dry_run?: boolean
}): Promise<{ task_arn: string; file_key: string }> {
  const response = await adminApiClient.post('/api/v1/admin/ingestion/run', params)
  return response.data
}

export async function getIngestionJobs(limit = 20): Promise<IngestionJob[]> {
  const response = await adminApiClient.get<IngestionJob[]>('/api/v1/admin/ingestion/jobs', { params: { limit } })
  return response.data
}

export async function submitIngestionJob(manifest_key: string): Promise<{ task_arn: string; file_key: string }> {
  const response = await adminApiClient.post('/api/v1/admin/ingestion/submit', { manifest_key })
  return response.data
}

export interface ViewCoverage {
  total_regulations: number
  views_generated: number
  missing: number
}

export async function getBackfillStatus(): Promise<{ task_arn: string | null }> {
  const response = await adminApiClient.get('/api/v1/admin/ingestion/backfill-status')
  return response.data
}

export async function stopEcsTask(task_arn: string): Promise<void> {
  await adminApiClient.post('/api/v1/admin/ingestion/stop-task', null, { params: { task_arn } })
}

export async function getViewCoverage(): Promise<ViewCoverage> {
  const response = await adminApiClient.get<ViewCoverage>('/api/v1/admin/ingestion/view-coverage')
  return response.data
}

export async function deleteIngestionJob(run_id: string, manifest_key: string): Promise<void> {
  await adminApiClient.delete(`/api/v1/admin/ingestion/jobs/${run_id}`, { params: { manifest_key } })
}

export async function generateRegulationViews(params: { dry_run?: boolean; pub_authority_uuid?: string; missing_only?: boolean }): Promise<{ task_arn: string; file_key: string }> {
  const response = await adminApiClient.post('/api/v1/admin/ingestion/generate-views', null, { params })
  return response.data
}

export async function generateSingleView(legislationUuid: string): Promise<{ task_arn: string; file_key: string }> {
  const response = await adminApiClient.post(`/api/v1/admin/ingestion/generate-view/${legislationUuid}`)
  return response.data
}

export async function processIngestionJob(manifest_key: string): Promise<{ task_arn: string; file_key: string }> {
  const response = await adminApiClient.post('/api/v1/admin/ingestion/process', { manifest_key })
  return response.data
}

export interface CoverageDoc {
  legislation_uuid: string
  title: string
  legal_ref: string
  language_code: string
  jurisdiction_uuids: string[]
  publishing_authority_uuids: string[]
}

export interface CoverageGroup {
  uuid: string
  name: string
  view: number
  pdf: number
  missing: number
}

export interface DocumentViewCoverage {
  summary: { total: number; view: number; pdf: number; missing: number }
  missing_docs: CoverageDoc[]
  by_jurisdiction: CoverageGroup[]
  by_pub_authority: CoverageGroup[]
}

export async function getDocumentViewCoverage(): Promise<DocumentViewCoverage> {
  const response = await adminApiClient.get<DocumentViewCoverage>('/api/v1/admin/database/document-view-coverage')
  return response.data
}

export async function getAdminDocuments(params: {
  page?: number
  page_size?: number
  search?: string
  jurisdiction_uuid?: string
  pub_authority_uuid?: string
}): Promise<PaginatedResponse<AdminDocument>> {
  const response = await adminApiClient.get<PaginatedResponse<AdminDocument>>(
    '/api/v1/admin/database/documents',
    { params },
  )
  return response.data
}

// ─── Standards Access ─────────────────────────────────────────────────────────

export interface StandardsAuthority {
  uuid: string
  code: string
  name: string
}

export interface StandardsUserEntry {
  user_id: string
  email?: string | null
}

// { authority_uuid: StandardsUserEntry[] }
export type StandardsAccessMap = Record<string, StandardsUserEntry[]>

export async function getStandardsAuthorities(): Promise<StandardsAuthority[]> {
  const response = await adminApiClient.get<StandardsAuthority[]>('/api/v1/admin/standards-authorities')
  return response.data
}

export async function setStandardsAuthorities(authorities: StandardsAuthority[]): Promise<void> {
  await adminApiClient.put('/api/v1/admin/standards-authorities', authorities)
}

export async function getStandardsAccess(): Promise<StandardsAccessMap> {
  const response = await adminApiClient.get<StandardsAccessMap>('/api/v1/admin/standards-access')
  return response.data
}

export async function addStandardsAccessUser(
  authorityUuid: string,
  userId: string,
  email?: string | null,
): Promise<void> {
  await adminApiClient.post(
    `/api/v1/admin/standards-access/${authorityUuid}/users/${userId}`,
    email ? { user_id: userId, email } : undefined,
  )
}

export async function removeStandardsAccessUser(authorityUuid: string, userId: string): Promise<void> {
  await adminApiClient.delete(`/api/v1/admin/standards-access/${authorityUuid}/users/${userId}`)
}
